import { ageText, formatWeight, isPreterm, type WeightUnit } from "@babia/checks";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { type Growth as GrowthModule, reasonWords, useGrowth } from "../../lib/growth.ts";
import {
  type GrowthRow,
  growthRows,
  growthSpan,
  type GrowthSpan,
  isPlotted,
  percentileCell,
  type StoredWeight,
  weighDay,
  weightScale,
  type WeightScale,
} from "../../lib/growth-chart.ts";
import type { Baby } from "../../lib/queries.ts";
import { clock } from "../../lib/time.ts";
import { GrowthChart } from "./growth-chart.tsx";
import { PerBaby, Section } from "./section.tsx";

const quiet = "mt-3 py-3 text-[14px] italic opacity-60";
const small = "mt-2 max-w-[60ch] text-[12px] leading-snug opacity-70";
const settings = (
  <Link to="/settings" hash="family" className="underline underline-offset-2">
    Settings
  </Link>
);

/** Every weight as text, newest first: the chart's plain-text twin. */
function WeightTable({
  baby,
  rows,
  unit,
  now,
}: {
  baby: Baby;
  rows: GrowthRow[];
  unit: WeightUnit;
  now: Date;
}) {
  const born = baby.birthDate;
  return (
    <details className="group mt-2">
      <summary className="ghost-btn -ml-2 inline-flex min-h-[44px] cursor-pointer items-center px-2 text-[14px]">
        <span className="group-open:hidden">Show the table</span>
        <span className="hidden group-open:inline">Hide the table</span>
      </summary>
      <table className="w-full border-collapse text-left text-[13px] tabular-nums">
        <thead>
          <tr className="border-b border-ink text-[11px] uppercase tracking-[0.06em]">
            <th className="py-1.5 pr-2 font-normal opacity-60">Date</th>
            <th className="py-1.5 pr-2 font-normal opacity-60">Age</th>
            <th className="py-1.5 pr-2 text-right font-normal opacity-60">Weight</th>
            <th className="py-1.5 text-right font-normal opacity-60">Percentile</th>
          </tr>
        </thead>
        <tbody>
          {[...rows].reverse().map((r) => (
            <tr key={r.id} className="border-t border-rule">
              <th scope="row" className="py-1.5 pr-2 font-normal whitespace-nowrap">
                {weighDay(r.at, now)}
              </th>
              <td className="py-1.5 pr-2">
                {born && r.date >= born ? ageText(born, r.date).replace(/ old$/, "") : "—"}
              </td>
              <td className="py-1.5 pr-2 text-right whitespace-nowrap">
                {formatWeight(r.grams, unit)}
              </td>
              <td className="py-1.5 text-right">
                {isPlotted(r) ? percentileCell(r.reading.percentile) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {born && isPreterm(born, baby.dueDate) ? (
        <p className={small}>Percentiles use corrected age, counted from the due date.</p>
      ) : null}
    </details>
  );
}

/** One baby's weights: the chart when the curves can place them, the reason when not, the table. */
function GrowthPanel({
  baby,
  rows,
  growth,
  failed,
  axes,
  unit,
  now,
}: {
  baby: Baby;
  rows: GrowthRow[];
  growth: GrowthModule | null;
  failed: boolean;
  axes: { span: GrowthSpan; scale: WeightScale } | null;
  unit: WeightUnit;
  now: Date;
}) {
  const latest = rows.at(-1);
  if (!latest)
    return <p className={quiet}>No weights logged yet. On the log, tap Note, then Weight.</p>;
  const plotted = rows.filter(isPlotted);
  const chart =
    growth && axes && baby.sex && plotted.length ? { growth, axes, sex: baby.sex } : null;
  const reason = latest.reading && "reason" in latest.reading ? latest.reading.reason : null;
  const early = rows.some(
    (r) => r.reading && "reason" in r.reading && r.reading.reason === "before-due-date",
  );
  // While the curves load, hold the chart's place when a weight will be on it.
  // The reasons need no curves, so they show at once, and after a failed load.
  const waiting = !failed && rows.some((r) => r.reading === null);

  return (
    <>
      {chart ? (
        <GrowthChart
          growth={chart.growth}
          sex={chart.sex}
          rows={plotted}
          span={chart.axes.span}
          scale={chart.axes.scale}
          unit={unit}
          name={baby.name}
          now={now}
        />
      ) : (
        <>
          <p className="mt-3 text-[15px] leading-snug tabular-nums">
            {weighDay(latest.at, now)} · {formatWeight(latest.grams, unit)}
          </p>
          {waiting ? <div className="mt-2 h-[260px]" aria-hidden="true" /> : null}
        </>
      )}
      {failed ? (
        <p className="mt-2 text-[13px] text-accent-2">
          Couldn't load the WHO curves. The weights are in the table.
        </p>
      ) : null}
      {reason ? (
        <p className={small}>
          {reasonWords(reason, baby.name, baby.dueDate)}
          {reason === "no-sex" || reason === "no-birth-date" ? <> Open {settings}.</> : null}
        </p>
      ) : null}
      {chart ? (
        <p className={small}>
          {early ? "Weights before the due date are in the table only. " : ""}
          Your pediatrician reads the curve over time, not one point. Curves: WHO Child Growth
          Standards.
        </p>
      ) : null}
      {baby.birthDate && !baby.dueDate ? (
        <p className={small}>Born more than 3 weeks early? Add the due date in {settings}.</p>
      ) : null}
      <WeightTable baby={baby} rows={rows} unit={unit} now={now} />
    </>
  );
}

/**
 * Each baby's weights on the WHO weight-for-age curves, after Week by week.
 * The WHO table downloads only for a household with a weight logged as a
 * number. Every baby shares one span and one scale, so a tab switch on a
 * phone keeps the axes still and twins compare like with like; each still
 * gets the curves for their own sex.
 */
export function Growth({
  babies,
  all,
  points,
  unit,
  loading,
  error,
  staleSince,
  now,
}: {
  /** The babies shown: one on a phone with tabs. */
  babies: Baby[];
  /** Every baby, for the shared axes. */
  all: Baby[];
  /** Every weight with a number, oldest first. */
  points: StoredWeight[];
  unit: WeightUnit;
  loading: boolean;
  /** Why the weights never loaded. */
  error: string | null;
  /** When the weights shown were loaded, if a refresh since then failed. */
  staleSince: number | null;
  now: Date;
}) {
  const { growth, failed } = useGrowth(points.length > 0);
  const rows = useMemo(
    () => new Map(all.map((b) => [b.id, growthRows(points, b, growth)])),
    [all, points, growth],
  );
  const axes = useMemo(() => {
    if (!growth) return null;
    const plotted = all.flatMap((b) => {
      const sex = b.sex;
      return sex === null
        ? []
        : (rows.get(b.id) ?? []).filter(isPlotted).map((row) => ({ sex, row }));
    });
    if (!plotted.length) return null;
    const span = growthSpan(Math.max(...plotted.map((p) => p.row.reading.days)));
    const sexes = [...new Set(plotted.map((p) => p.sex))];
    const grams = plotted.map((p) => p.row.grams);
    return { span, scale: weightScale(growth, sexes, span.end, grams, unit) };
  }, [growth, all, rows, unit]);

  return (
    <Section title="Growth" note="Weight for age · WHO standards">
      {error ? (
        <p className="mt-3 text-[14px] text-accent-2">Couldn't load the weights — {error}.</p>
      ) : staleSince !== null ? (
        <p className="mt-3 text-[13px] opacity-70">
          Couldn't refresh the weights — showing them as of {clock(new Date(staleSince))}.
        </p>
      ) : null}
      {error ? null : (
        <PerBaby babies={babies}>
          {(b) =>
            loading ? (
              <p className={quiet}>Loading weights…</p>
            ) : (
              <GrowthPanel
                baby={b}
                rows={rows.get(b.id) ?? []}
                growth={growth}
                failed={failed}
                axes={axes}
                unit={unit}
                now={now}
              />
            )
          }
        </PerBaby>
      )}
    </Section>
  );
}
